/* Operational Verification panel — surfaces the production-readiness
 * guarantees the admin / investors care about:
 *   1. Checkout uses keys read from server (env or admin-saved DB).
 *   2. Download routes refuse to serve plan PDFs unless the linked
 *      purchase is verified.
 *   3. Trust elements (refund / privacy / terms / support phone)
 *      resolve on the public site.
 *
 * Backed by GET /api/admin/ops-verification (admin-gated).
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  KeyRound,
  Download,
  ExternalLink,
  PhoneCall,
  MessageCircle,
  RefreshCw,
} from "lucide-react";
import { adminGet } from "@/lib/adminApi";

const Row = ({ ok, icon: Icon, label, detail, testId, children }) => (
  <div
    data-testid={testId}
    className={`rounded-2xl border-2 px-4 py-3 ${
      ok
        ? "bg-emerald-50 border-emerald-200"
        : "bg-amber-50 border-amber-200"
    }`}
  >
    <div className="flex items-start gap-2.5">
      <Icon
        className={`h-5 w-5 mt-0.5 shrink-0 ${
          ok ? "text-emerald-600" : "text-amber-600"
        }`}
        strokeWidth={2.4}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-extrabold text-navy text-[13.5px]">{label}</div>
          {ok ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
          ) : (
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
          )}
        </div>
        <div className="text-[12px] text-muted-foreground mt-0.5 leading-snug">
          {detail}
        </div>
        {children}
      </div>
    </div>
  </div>
);

const OpsVerificationPanel = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await adminGet("/admin/ops-verification");
    if (res.ok) {
      setData(res.data);
    } else {
      setData(null);
      setError({ status: res.status, message: res.error });
    }
    setLoading(false);
  }, []);

  const runDiagnostics = useCallback(async () => {
    setDiagLoading(true);
    const res = await adminGet("/admin/diagnostics");
    setDiagnostics(res.ok ? res.data : { error: res.error, status: res.status });
    setDiagLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <section
        className="mb-8 rounded-3xl bg-white border border-border p-6 text-center text-muted-foreground"
        data-testid="admin-ops-verification-loading"
      >
        Running operational checks…
      </section>
    );
  }

  if (!data) {
    return (
      <section
        className="mb-8 rounded-3xl bg-amber-50 border-2 border-amber-300 p-6"
        data-testid="admin-ops-verification-error"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-extrabold text-amber-900">
              Operational checks failed to load
            </h3>
            <p className="text-[13px] text-amber-900/90 mt-1">
              {error?.message || "Unable to reach the admin backend."}{" "}
              {error?.status ? <span className="font-mono text-[11px]">(HTTP {error.status})</span> : null}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={load}
                data-testid="admin-ops-retry"
                className="min-h-[40px] px-3 rounded-xl bg-navy text-gold font-extrabold text-[12.5px] inline-flex items-center gap-1.5 hover:bg-navy-deep"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Retry checks
              </button>
              <button
                onClick={runDiagnostics}
                disabled={diagLoading}
                data-testid="admin-ops-run-diagnostics"
                className="min-h-[40px] px-3 rounded-xl bg-white border-2 border-navy text-navy font-extrabold text-[12.5px] inline-flex items-center gap-1.5 hover:bg-navy/5 disabled:opacity-60"
              >
                {diagLoading ? "Probing…" : "Run production diagnostics"}
              </button>
            </div>
            {diagnostics && (
              <details
                open
                className="mt-3 rounded-xl bg-white border border-amber-200 p-3"
                data-testid="admin-ops-diagnostics-output"
              >
                <summary className="text-[12px] font-extrabold text-navy cursor-pointer">
                  Diagnostics payload (env presence, cookies, DB)
                </summary>
                <pre className="mt-2 text-[11px] leading-snug text-navy whitespace-pre-wrap break-all max-h-[400px] overflow-auto">
                  {JSON.stringify(diagnostics, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </div>
      </section>
    );
  }

  const checkoutLabel =
    data.checkout_keys.source === "environment"
      ? "Checkout Keys Source: Environment"
      : data.checkout_keys.source === "database"
      ? "Checkout Keys Source: Database (admin-saved, encrypted)"
      : "Checkout Keys Source: Not configured";

  return (
    <section
      className="mb-8 rounded-3xl bg-white border-2 border-navy/15 shadow-[0_24px_48px_-24px_rgba(11,37,69,0.20)] overflow-hidden"
      data-testid="admin-ops-verification"
    >
      <header className="bg-gradient-to-r from-navy via-navy-deep to-navy text-white p-5 flex items-center gap-3 relative">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />
        <div className="h-12 w-12 rounded-2xl bg-gold text-navy grid place-items-center shrink-0 shadow-md">
          <ShieldCheck className="h-6 w-6" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold font-bold">
            Production · Readiness Snapshot
          </div>
          <h2 className="text-xl font-extrabold tracking-tight">
            Operational Verification
          </h2>
        </div>
      </header>

      <div className="p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Row
          ok={data.checkout_keys.ok}
          icon={KeyRound}
          label={checkoutLabel}
          detail="Razorpay credentials are read from the backend only — frontend bundles never see the secret."
          testId="ops-checkout-keys"
        >
          <div className="mt-1.5 text-[11.5px]">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-extrabold uppercase tracking-wider text-[10px] ${
                data.checkout_keys.razorpay_active
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-amber-100 text-amber-700"
              }`}
              data-testid="ops-checkout-keys-razorpay"
            >
              Razorpay client {data.checkout_keys.razorpay_active ? "active" : "inactive"}
            </span>
          </div>
        </Row>

        <Row
          ok={data.download_protection.ok}
          icon={Download}
          label="Download Protection: Active"
          detail={data.download_protection.rule}
          testId="ops-download-protection"
        >
          <ul className="mt-1.5 space-y-0.5 text-[11px] text-muted-foreground font-mono">
            {data.download_protection.enforced_gates.map((g) => (
              <li
                key={g}
                className="truncate"
                data-testid={`ops-download-gate-${g.length}`}
              >
                • {g}
              </li>
            ))}
          </ul>
        </Row>

        <Row
          ok={data.trust_elements.ok}
          icon={ShieldCheck}
          label="Trust Elements: All Live"
          detail="Refund / Privacy / Terms pages + support contacts are wired."
          testId="ops-trust-elements"
        >
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {data.trust_elements.pages.map((p) => (
              <a
                key={p.path}
                href={p.path}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`ops-trust-link-${p.path.replace(/\//g, "")}`}
                className="inline-flex items-center gap-1 text-[10.5px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full bg-navy/5 text-navy hover:bg-navy/10"
              >
                {p.label}
                <ExternalLink className="h-2.5 w-2.5" />
              </a>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-3 text-[11.5px] text-navy">
            <a
              href={`tel:${data.trust_elements.support_phone.replace(/\s+/g, "")}`}
              data-testid="ops-trust-phone"
              className="inline-flex items-center gap-1 font-bold hover:text-gold-dark"
            >
              <PhoneCall className="h-3 w-3" /> {data.trust_elements.support_phone}
            </a>
            <a
              href={`https://wa.me/${data.trust_elements.support_whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ops-trust-whatsapp"
              className="inline-flex items-center gap-1 font-bold hover:text-gold-dark"
            >
              <MessageCircle className="h-3 w-3" /> WhatsApp
            </a>
          </div>
        </Row>
      </div>
    </section>
  );
};

export default OpsVerificationPanel;
